import { Component, inject, OnInit, TemplateRef } from '@angular/core';
import { MappingService } from "../../services/mapping/mapping.service";
import { SupplierSystemsService } from "../../services/supplier-systems/supplier-systems.service";
import { CommonModule } from "@angular/common";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzTableModule } from "ng-zorro-antd/table";
import { FormsModule } from "@angular/forms";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzModalModule, NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { FeatureComponent } from "../features/feature/feature.component";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  Mapping,
  MappingInput,
  MappingAttribute,
  MappingScopeType,
  MAPPING_SCOPE_TYPES,
  VehicleBrand,
  VEHICLE_BRAND_MAP,
  MappingQueryParams,
  MAPPING_ATTRIBUTES,
  MAPPING_TYPES
} from '../../model/models';
import { YomaDateFormatPipe } from "../../pipes/yoma-date-format.pipe";

@Component({
  selector: 'mapping',
  standalone: true,
  imports: [CommonModule, FormsModule, NzIconModule, NzTableModule, NzButtonModule, NzFormModule, FormsModule, NzModalModule, FeatureComponent, NzSpinModule, NzSelectModule, NzTypographyModule, NzDropDownModule, NzInputModule, NzToolTipModule, YomaDateFormatPipe],
  templateUrl: './mapping.component.html',
  styleUrl: './mapping.component.scss'
})
export class MappingComponent implements OnInit {
  mappingService = inject(MappingService);
  supplierSystemsService = inject(SupplierSystemsService);
  modal = inject(NzModalService);

  loading = false;
  listOfData = new Array<Mapping>();
  deleteTextValue = ""

  attributes = MAPPING_ATTRIBUTES;
  scopeTypes = MAPPING_SCOPE_TYPES;
  brands = Object.entries(VEHICLE_BRAND_MAP).map(([value, label]) => ({ value, label: label as VehicleBrand }));
  supplierSystems: { value: string; label: string }[] = [];
  supplierSystemsLoading = false;
  mappingTypes = MAPPING_TYPES;
  mappingFilter: Partial<MappingQueryParams> & { attribute?: MappingAttribute; scope_type: MappingScopeType; scope_value?: string } = { scope_type: 'BRAND' };
  regexTestResult: string | null = null;

  nextToken?: string;
  tokenHistory: (string | undefined)[] = [];
  private currentToken?: string;

  ngOnInit(): void {
    this.supplierSystemsLoading = true;
    this.supplierSystemsService.getSupplierSystems().subscribe({
      next: (res: any) => {
        this.supplierSystems = (Array.isArray(res) ? res : [])
          .filter((s: any) => s?.supplier_system_partner_id)
          .map((s: any) => ({
            value: s.supplier_system_partner_id,
            label: s.supplier_system_partner_id + (s.supplier_system_partner_name ? ` (${s.supplier_system_partner_name})` : '')
          }))
          .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
        this.supplierSystemsLoading = false;
      },
      error: () => this.supplierSystemsLoading = false
    });
  }

  scopeOptions(scopeType: MappingScopeType | undefined): { value: string; label: string }[] {
    return scopeType === 'SYSTEM' ? this.supplierSystems : this.brands;
  }

  scopeLabel(scopeType: MappingScopeType | undefined): string {
    return scopeType === 'SYSTEM' ? 'Supplier system' : 'Brand';
  }

  /** Human readable scope for a stored mapping (brand display name or supplier system partner id). */
  scopeDisplay(item: Mapping): string {
    if (item.scope_type === 'SYSTEM') {
      return item.supplier_system_partner_id ?? '';
    }
    return item.brand
      ?? (item.brand_normalized ? this.brandLabel(item.brand_normalized) ?? item.brand_normalized : '');
  }

  onFilterScopeTypeChange(): void {
    this.mappingFilter.scope_value = undefined;
  }

  onModalScopeTypeChange(data: any): void {
    data.scope_value = undefined;
  }

  search(): void {
    if (!this.mappingFilter.attribute || !this.mappingFilter.scope_type || !this.mappingFilter.scope_value) {
      return;
    }
    this.tokenHistory = [];
    this.fetchPage(undefined);
  }

  loadNextPage(): void {
    if (!this.nextToken) {
      return;
    }
    this.tokenHistory.push(this.currentToken);
    this.fetchPage(this.nextToken);
  }

  loadPreviousPage(): void {
    this.fetchPage(this.tokenHistory.pop());
  }

  private fetchPage(token: string | undefined): void {
    this.loading = true;
    const { attribute, scope_type, scope_value, ...queryParams } = this.mappingFilter;
    this.mappingService.getMappings(attribute!, scope_type, scope_value!, { ...queryParams, next_token: token } as MappingQueryParams).subscribe({
      next: res => {
        this.listOfData = res.items;
        this.nextToken = res.next_token;
        this.currentToken = token;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  openMappingModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, item?: Mapping): void {
    const data = item
      ? { ...JSON.parse(JSON.stringify(item)), scope_value: this.scopeValueOf(item), _isEdit: true }
      : {
          _isEdit: false,
          attribute: this.mappingFilter.attribute,
          scope_type: this.mappingFilter.scope_type,
          scope_value: this.mappingFilter.scope_value
        };
    const modal: NzModalRef = this.modal.create({
      nzTitle: item ? 'Edit Mapping' : 'Add new Mapping',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzData: data
    });
  }

  saveMapping(ref: NzModalRef): void {
    this.loading = true;
    const data = ref.getConfig().nzData;
    const body: MappingInput = {
      brand: data.scope_type === 'BRAND' ? this.brandLabel(data.scope_value) : undefined,
      mapping_type: data.mapping_type,
      raw_value: data.raw_value,
      normalized_value: data.normalized_value,
      exclude_value: data.mapping_type === 'REGEX_MAPPING' ? data.exclude_value : undefined
    };
    this.mappingService.createMapping(data.attribute, data.scope_type, data.scope_value, body).subscribe({
      next: () => {
        this.loading = false;
        ref.close();
        this.search();
      },
      error: () => this.loading = false
    });
  }

  deleteMappingModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>, item: Mapping): void {
    const modal: NzModalRef = this.modal.create({
      nzTitle: 'Delete mapping',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzData: item,
    });
    modal.afterClose.subscribe(() => this.deleteTextValue = '');
  }

  deleteMapping(ref: NzModalRef): void {
    this.loading = true;
    const data = ref.getConfig().nzData;
    this.mappingService.deleteMapping(data.attribute, data.scope_type, this.scopeValueOf(data), {
      mapping_type: data.mapping_type,
      raw_value: data.raw_value
    }).subscribe({
      next: () => {
        this.loading = false;
        ref.close();
        this.search();
      },
      error: () => this.loading = false
    });
  }

  private scopeValueOf(item: Mapping): string {
    if (item.scope_type === 'SYSTEM') {
      return item.supplier_system_partner_id ?? this.mappingFilter.scope_value ?? '';
    }
    return item.brand_normalized
      ?? this.brands.find(b => b.label === item.brand)?.value
      ?? this.mappingFilter.scope_value
      ?? '';
  }

  private brandLabel(normalized: string): VehicleBrand | undefined {
    return (VEHICLE_BRAND_MAP as Record<string, VehicleBrand>)[normalized];
  }

  testRegex(): void {
    // TODO: call BE regex validation endpoint once available
    this.regexTestResult = null;
  }

}
